import { useEffect, useState, useCallback } from "react";
import Camera from "./Camera";
import VoiceInput from "./VoiceInput";
import PermissionGate from "./PermissionGate";
import { askMoonDream } from "../utils/api";
import '../App.css';
import { Link } from "react-router-dom";
import fileIcon from '../assets/file.png';
import logo from '../assets/logo_copiaazul.png';
import ImageUpload from './ImageUpload';
import webcamIcon from '../assets/webcam.png';

const BlindAssistant = () => {
  const [answer, setAnswer] = useState("");
  const [image, setImage] = useState(null);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [contextSensitivity, setContextSensitivity] = useState(50);
  const [confidenceLevel, setConfidenceLevel] = useState(50);
  const [uploadedImage, setUploadedImage] = useState(null);

  // Initialize speech synthesis on component mount
  useEffect(() => {
    const initSpeech = () => {
      const utterance = new SpeechSynthesisUtterance('');
      utterance.volume = 0;
      window.speechSynthesis.speak(utterance);
    };

    const handleInteraction = () => {
      initSpeech();
      document.removeEventListener('click', handleInteraction);
    };

    document.addEventListener('click', handleInteraction);
    return () => document.removeEventListener('click', handleInteraction);
  }, []);

  const speak = useCallback((text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-US';

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsSpeaking(false);
    };

    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 100);

    const handleVisibilityChange = () => {
      if (!document.hidden && isSpeaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const resumeInfinity = setInterval(() => {
      if (isSpeaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 10000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(resumeInfinity);
      window.speechSynthesis.cancel();
    };
  }, [isSpeaking]);

  const handleImageUpload = (base64Image) => {
    setUploadedImage(base64Image);
  };

  const handleQuestionSubmit = async (question) => {
    try {
      let imageBase64;
      
      if (uploadedImage) {
        // Use the uploaded image if available
        imageBase64 = uploadedImage;
      } else {
        // Use webcam image if no upload
        const webcam = document.querySelector("video");
        const canvas = document.createElement("canvas");
        canvas.width = webcam.videoWidth;
        canvas.height = webcam.videoHeight;
        canvas.getContext("2d").drawImage(webcam, 0, 0);
        imageBase64 = canvas.toDataURL("image/jpeg").split(",")[1];
      }

      const response = await askMoonDream(imageBase64, question);
      setAnswer(response);
      speak(response);
    } catch (error) {
      console.error('Error processing question:', error);
      const errorMessage = "Sorry, there was an error processing your question. Please try again.";
      setAnswer(errorMessage);
      speak(errorMessage);
    }
  };

  const handleSwitchToWebcam = () => {
    setUploadedImage(null);
  };

  return (
    <div className="app-container">
      <nav className="nav-bar">
        <div className="nav-brand">
          <Link to="/" className="brand-link">
            <img src={logo} alt="WCAS Logo" className="nav-logo" />
            <span className="nav-title" style={{
              background: 'linear-gradient(to bottom, #ffffff, #66f2f2)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>What-can-AI-see?</span>
          </Link>
        </div>
      </nav>

      {!permissionsGranted ? (
        <PermissionGate onPermissionsGranted={() => setPermissionsGranted(true)} />
      ) : (
        <div className="app-content">
          <div className="camera-section">
            {uploadedImage ? (
              <>
                <img 
                  src={`data:image/jpeg;base64,${uploadedImage}`}
                  alt="Uploaded"
                  className="uploaded-image"
                />
                <div className="camera-controls">
                  <button className="webcam-button" onClick={handleSwitchToWebcam}>
                    <img src={webcamIcon} alt="Webcam" className="webcam-icon" />
                    Webcam
                  </button>
                  <ImageUpload onImageUpload={handleImageUpload} />
                </div>
              </>
            ) : (
              <>
                <Camera />
                <ImageUpload onImageUpload={handleImageUpload} />
              </>
            )}
          </div>
          <div className="interaction-section">
            <div className="sliders-container">
              <div className="slider-group">
                <label>
                  Context Sensitivity
                  <div className="slider-labels">
                    <span>Literal</span>
                    <span>Context-Aware</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={contextSensitivity}
                    onChange={(e) => setContextSensitivity(e.target.value)}
                    className="custom-slider"
                  />
                </label>
              </div>
              <div className="slider-group">
                <label>
                  Confidence Level
                  <div className="slider-labels">
                    <span>Cautious</span>
                    <span>Assertive</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={confidenceLevel}
                    onChange={(e) => setConfidenceLevel(e.target.value)}
                    className="custom-slider"
                  />
                </label>
              </div>
            </div>
            <div className="voice-input-container">
              <VoiceInput onQuestionSubmit={handleQuestionSubmit} />
            </div>
            {answer && (
              <div className="answer-container">
                <p className="answer-text">
                  {isSpeaking && <span className="speaking-indicator">🔊 </span>}
                  Answer: {answer}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlindAssistant; 